import {
  Box, Button, ButtonIcon, ButtonText, Icon,
} from '@gluestack-ui/themed';
import { Hand, MicOff } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

type BriefingHandIndicatorProps = {
  identity: string;
  raised: boolean;
  interactive?: boolean;
  onToggle?: (raised: boolean) => void;
};

/**
 * Shared raised-hand control/indicator used by the live group briefing and Visual Lab.
 */
export function BriefingHandIndicator({
  identity,
  raised,
  interactive = false,
  onToggle,
}: BriefingHandIndicatorProps) {
  const { t } = useTranslation();

  if (!interactive && !raised) return null;

  if (interactive) {
    return (
      <Button
        variant="outline"
        action="secondary"
        size="sm"
        onPress={() => onToggle?.(!raised)}
        accessibilityState={{ selected: raised }}
        accessibilityLabel={raised
          ? t('oralExam.groupBriefing.lowerHand', { defaultValue: 'Lower hand' })
          : t('oralExam.groupBriefing.raiseHand', { defaultValue: 'Raise hand' })}
        testID={`briefing-raise-hand-${identity}`}
      >
        <ButtonIcon as={Hand} size="xs" mr="$1" testID="briefing-hand-icon" />
        <ButtonText>
          {raised
            ? t('oralExam.groupBriefing.lowerHand', { defaultValue: 'Lower hand' })
            : t('oralExam.groupBriefing.raiseHand', { defaultValue: 'Raise hand' })}
        </ButtonText>
      </Button>
    );
  }

  return (
    <Box
      width={36}
      height={36}
      borderRadius="$full"
      bg="$statusWarning"
      borderWidth={1}
      borderColor="$statusWarning"
      alignItems="center"
      justifyContent="center"
      pointerEvents="none"
      accessibilityLabel={t('oralExam.groupBriefing.handRaised', {
        defaultValue: 'Hand raised',
      })}
      testID={`briefing-hand-raised-${identity}`}
    >
      <Icon
        as={Hand}
        size="sm"
        color="$textOnWarning"
        testID="briefing-hand-icon"
      />
    </Box>
  );
}

type BriefingStudentMutedIndicatorProps = {
  identity: string;
  muted: boolean;
};

/**
 * Status-only muted badge rendered over a group-briefing participant tile.
 */
export function BriefingStudentMutedIndicator({
  identity,
  muted,
}: BriefingStudentMutedIndicatorProps) {
  if (!muted) return null;
  return (
    <Box
      position="absolute"
      top="$1"
      left="$1"
      width={32}
      height={32}
      borderRadius="$full"
      bg="$bgCard"
      borderWidth={1}
      borderColor="$borderLight"
      alignItems="center"
      justifyContent="center"
      pointerEvents="none"
      testID={`briefing-student-muted-${identity}`}
    >
      <Icon as={MicOff} size="sm" color="$statusError" />
    </Box>
  );
}

type BriefingStudentAudioButtonProps = {
  identity: string;
  muted: boolean;
  onToggle: (muted: boolean) => void;
};

/**
 * Standard Flare action button for one student's group-briefing microphone.
 */
export function BriefingStudentAudioButton({
  identity,
  muted,
  onToggle,
}: BriefingStudentAudioButtonProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      action="secondary"
      size="sm"
      onPress={() => onToggle(!muted)}
      accessibilityLabel={muted
        ? t('oralExam.lecturer.groupBriefing.unmute', { defaultValue: 'Unmute' })
        : t('oralExam.lecturer.groupBriefing.mute', { defaultValue: 'Mute' })}
      testID={`briefing-allow-speak-${identity}`}
    >
      <ButtonText>
        {muted
          ? t('oralExam.lecturer.groupBriefing.unmute', { defaultValue: 'Unmute' })
          : t('oralExam.lecturer.groupBriefing.mute', { defaultValue: 'Mute' })}
      </ButtonText>
    </Button>
  );
}
